import { Component, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { PharmaDataService } from "src/app/pharma-page/pharma-data.service";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import {
  debounceTime,
  switchMap,
  distinctUntilChanged,
  map,
} from "rxjs/operators";
import { FilterEventChanged } from "src/app/store/actions/pharma-page.actions";
import {
  FacetFilter,
  SearchCategories,
} from "src/app/pharma-page/pharma-page.model";
import {
  plus,
  externalLink,
  search,
  VAIconRegistryService,
} from "@va/core-library/icons";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { Location } from "@angular/common";
import { Router } from "@angular/router";

@Component({
  selector: "app-biopharma-search",
  templateUrl: "./biopharma-search.component.html",
  styleUrls: ["./biopharma-search.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class BiopharmaSearchComponent implements OnInit {
  myControl = new FormControl();
  options: any[] = [];
  filteredOptions: Observable<any[]>;
  searchString = "";
  matchQueryString = "";
  facetFilters = {};
  minLengthTerm = 1;
  pharmaDatabasePageSubscriber: Subscription;
  pageNameSubscriber: Subscription;
  accessSubscriber: Subscription;
  searchQuery: string;
  categoryNames = SearchCategories;
  showResult = false;
  showDbLink = false;
  pageName = "";
  hasDrugPageAccess = false;
  routeSubscriber: Subscription;
  pillsData;
  @ViewChild(MatAutocompleteTrigger, { static: false })
  autocomplete: MatAutocompleteTrigger;

  constructor(
    private pharmaService: PharmaDataService,
    private iconRegistryService: VAIconRegistryService,
    private store: Store<AppState>,
    private router: Router,
    private _location: Location,
  ) {
    this.iconRegistryService.registerIcons([plus, externalLink, search]);
    this.searchQuery = "";
  }
  ngOnInit() {
    this.pharmaDatabasePageSubscriber = this.store
      .select(stateGetter("pharmaDatabasePage"))
      .subscribe((data: any) => {
        this.facetFilters = data.facetFilters;
        this.pillsData = data.pillsData;
      });

    this.filteredOptions = this.myControl.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(100),
      switchMap((value) => this._filter(value)),
    );
    this.pageNameSubscriber = this.store
      .select(stateGetter("globalSubheader", "pageName"))
      .subscribe((data: any) => {
        this.showDbLink = data === "biopharma-drug";
        this.pageName = data;
      });
    this.accessSubscriber = this.store
      .select(stateGetter("globalHeader", "globalSettings", "pharmaAccess"))
      .subscribe((data: any) => {
        if (data && data.hasBiopharmaDrugPageAccess)
          this.hasDrugPageAccess = data.hasBiopharmaDrugPageAccess;
      });
  }

  private _filter(value: string) {
    const filterValue = value.toLowerCase();

    const context = {};
    for (const key in this.facetFilters) {
      if (this.facetFilters[key].length) {
        context[key] = [...this.facetFilters[key]];
      }
    }
    if (this.facetFilters["include_terminated"] === true) {
      context["include_terminated"] = true;
    }

    return this.pharmaService
      .getBiopharmaSearchResults({ query: filterValue, context: context })
      .pipe(
        map((resp: any) => {
          if (value === "") {
            this.pharmaService.logUserAction(
              "ALL_FACET_SEARCH",
              {
                query_string: value,
              },
              this.pageName === "biopharma-drug"
                ? "BIOPHARMA_DRUG_PAGE"
                : "BIOPHARMA_DB",
            );
          }
          return this.formatDisplayName(resp.data);
        }),
      );
  }

  addFilter(option) {
    this.pharmaService.logUserAction(
      "SEARCH_ADD_FILTER",
      {
        ...option,
      },
      this.pageName === "biopharma-drug"
        ? "BIOPHARMA_DRUG_PAGE"
        : "BIOPHARMA_DB",
    );
    //update facet filter and raise store event
    this.updateStoreOnAddFilter(option);
    this.closePanel();
  }

  addCompany(option) {
    this.facetFilters["company_id"].push(option.id);
    return {
      [option.id]: option.ticker,
    };
  }
  addDrug(option) {
    this.facetFilters["drug"].push(option.id);
    return {
      [option.id]: option.type === "alias" ? option.drug : option.displayName,
    };
  }

  updateStoreOnAddFilter(option) {
    const category = option.type === "alias" ? "drug" : option.type;
    const extraInfo = {
      ...this.pillsData.extraInfo,
    };
    if (category === "ticker" || category === "companyname") {
      extraInfo.ticker_data = {
        ...extraInfo.ticker_data,
        ...this.addCompany(option),
      };
    } else if (category === "drug") {
      extraInfo.drug_data = {
        ...extraInfo.drug_data,
        ...this.addDrug(option),
      };
    } else if (category === "phase" && option.id === 7) {
      const facetFiltersCopy = { ...this.facetFilters };
      facetFiltersCopy["include_terminated"] = true;
      this.facetFilters = JSON.parse(JSON.stringify(facetFiltersCopy));
    } else if (category === "commercial_status" && option.id === 7) {
      const facetFiltersCopy = { ...this.facetFilters };
      facetFiltersCopy["include_terminated"] = true;
      facetFiltersCopy["commercial_status"] = [
        ...facetFiltersCopy["commercial_status"],
        7,
      ];
      if (!extraInfo[`commercial_status_data`]) {
        extraInfo[`commercial_status_data`] = {};
      }
      extraInfo[`commercial_status_data`] = {
        ...extraInfo[`commercial_status_data`],
        7: "Discontinued",
      };
      this.facetFilters = JSON.parse(JSON.stringify(facetFiltersCopy));
    } else {
      const facetFiltersCopy = { ...this.facetFilters };
      facetFiltersCopy[category] = [...facetFiltersCopy[category], option.id];
      this.facetFilters = JSON.parse(JSON.stringify(facetFiltersCopy));
      if (!extraInfo[`${category}_data`]) {
        extraInfo[`${category}_data`] = {};
      }
      extraInfo[`${category}_data`] = {
        ...extraInfo[`${category}_data`],
        [option.id]: option.value,
      };
    }

    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "facet",
        source: "search",
        data: {
          facetFilters: this.facetFilters,
          extraInfo: {
            ...extraInfo,
          },
        },
      }),
    );
  }

  createNewFilter(option) {
    try {
      //redirect with option set so it works for all pages
      this.facetFilters = new FacetFilter().toDTO();
      const category =
        option.type === "indications" ? "indication" : option.type;
      const filterCategory =
        option.type === "ticker"
          ? "company_id"
          : option.type === "alias"
            ? "drug"
            : option.type;
      const extraInfo = {
        ticker_data: {},
        drug_data: {},
      };
      let queryParams = {};
      if (category === "ticker" || category === "companyname") {
        extraInfo.ticker_data = this.addCompany(option);
        queryParams =
          category === "companyname"
            ? { company_id: option.id }
            : { [filterCategory]: option.id };
      } else if (category === "drug" || category === "alias") {
        extraInfo.drug_data = this.addDrug(option);
        queryParams = { [filterCategory]: option.id };
      } else if (category === "phase" && option.id === 7) {
        this.facetFilters["include_terminated"] = true;
        queryParams = { include_terminated: true };
      } else if (category === "commercial_status" && option.id === 7) {
        if (!extraInfo[`commercial_status_data`]) {
          extraInfo[`commercial_status_data`] = {};
        }
        extraInfo[`commercial_status_data`] = {
          ...extraInfo[`commercial_status_data`],
          7: "Discontinued",
        };
        this.facetFilters["include_terminated"] = true;
        this.facetFilters["commercial_status"] = [
          ...this.facetFilters["commercial_status"],
          7,
        ];
        queryParams = { commercial_status: 7, include_terminated: true };
      } else {
        this.facetFilters[category].push(option.id);
        if (!extraInfo[`${category}_data`]) {
          extraInfo[`${category}_data`] = {};
        }
        extraInfo[`${category}_data`] = {
          [option.id]: option.value,
        };
        queryParams = { [filterCategory]: option.id };
      }
      this.store.dispatch(
        new FilterEventChanged({
          filterChanged: "facet",
          source: "new-search",
          data: {
            facetFilters: this.facetFilters,
            savedFilter: {
              filter_id: null,
              filter_name: "",
              json_data: {},
            },
            extraInfo: {
              ...extraInfo,
            },
          },
        }),
      );
      this.closePanel();
      if (this.pageName === "biopharma-database") {
        this.router.navigate([`/biopharma/database/filter`], {
          queryParams: queryParams,
        });
      }
      //redirect to db
      this.pharmaService.logUserAction(
        "SEARCH_NEW_FACET",
        {
          ...option,
        },
        this.pageName === "biopharma-drug"
          ? "BIOPHARMA_DRUG_PAGE"
          : "BIOPHARMA_DB",
      );
      let key = category;
      let val = option.id;
      if (category === "ticker" || category === "companyname") {
        key = "company_id";
        val = option.id;
      } else if (category === "drug" || category === "alias") {
        key = "drug";
        val = option.id;
      }
      if (this.showDbLink) {
        setTimeout(() => {
          window.open(`/biopharma/database/filter?${key}=${val}`, "_blank");
        }, 100);
      }
    } catch (err) {
      console.error("Filter error :", err);
    }
  }
  displayFn() {
    return "";
  }
  clearSearch() {
    setTimeout(() => {
      this.searchQuery = "";
    }, 250);
  }
  closePanel() {
    this.searchQuery = "";
    this.autocomplete.closePanel();
  }
  redirectToDrugPage(option) {
    this.closePanel();
    this.pharmaService.logUserAction(
      "SEARCH_DRUG_PAGE_REDIRECT",
      {
        ...option,
      },
      this.pageName === "biopharma-drug"
        ? "BIOPHARMA_DRUG_PAGE"
        : "BIOPHARMA_DB",
    );
    window.open(`/biopharma/drug/${option.id}`, "_blank");
  }

  formatDisplayName(data) {
    const newValues = data.map((val) => {
      val["displayName"] = val.value;
      if (val.type === "drug") {
        val.displayName = `${val.value} | ${val.ticker}`;
      }
      if (val.type === "alias") {
        val.displayName = `${val.drug} | ${val.ticker} alias : ${val.value}`;
      }
      if (val.type === "ticker" || val.type === "companyname") {
        val.displayName = `${val.ticker ? val.ticker : val.value} | ${
          val.fullname ? val.fullname : val.value
        }`;
      }
      return val;
    });

    if (
      this.searchQuery.length > 1 &&
      this.searchQuery.charAt(this.searchQuery.length - 1) === "\\"
    ) {
      this.matchQueryString = this.searchQuery.substring(
        0,
        this.searchQuery.length - 1,
      );
    } else {
      this.matchQueryString = this.searchQuery;
    }
    return newValues;
  }
}
